<template>
    <LoadingPanel
        :headline="t('transformers.conditionStatus.content.latestReports.title')"
        :is-pending="isPending"
        :is-error="isError"
        :hide-content="isExportLayout && data?.latestConditionStatuses?.length === 0"
    >
        <template
            v-if="isExportLayout && data?.latestConditionStatuses?.length === 0"
            #context
        >
            <ColorBadge
                :color="StatusTypes[ConditionStatus.None].color"
                :text="t(StatusTypes[ConditionStatus.None].labelKey)"
                small
            />
        </template>
        <div
            v-if="data && data.latestConditionStatuses?.length !== 0"
            :class="['latest-reports', { 'latest-reports--export-layout': isExportLayout }]"
        >
            <div ref="tableRef" />
        </div>
        <EmptyStatePanelContent v-else />
    </LoadingPanel>
</template>

<script setup lang="ts">
import { useComponentStatusQuery } from '@/api/queries/general/useComponentStatusQuery'
import { computed, ref, watch, toRefs } from 'vue'
import { PebbleTable } from '@pebble/tables'
import { CellComponent, Options } from 'tabulator-tables'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { AnalysisTypes } from '@/configuration/analysis/analysis-types'
import { useI18n } from 'vue-i18n'
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { ConditionStatus, StatusTypes } from '@/configuration/status/condition-status'
import ColorBadge from '@/components/badge/ColorBadge.vue'
import { ComponentType } from '@/configuration/component/component-types'
import { MeasurementTypes } from '@/configuration/measurements/measurement-types'
import { createElement, Pencil } from 'lucide'
import { useEditNextReportDateModal } from '@/components/modals/edit-next-report-date/useEditNextReportDateModal'
import { usePermissions } from '@/composables/usePermissions'

const tableDimensions = {
    headerHeight: 43,
    rowHeight: 34,
    numberOfVisibleRows: 7,
} as const

const tableHeights = {
    normal: tableDimensions.headerHeight + tableDimensions.rowHeight * tableDimensions.numberOfVisibleRows,
    exportLayout: '100%',
}

const props = defineProps<{
    isExportLayout?: boolean
    componentId: string
    componentType: ComponentType
}>()

const { isExportLayout, componentId, componentType } = toRefs(props)

const { t, locale } = useI18n()
const { userPermissions } = usePermissions()

const { data, isPending, isError } = useComponentStatusQuery(componentId.value, componentType.value)

const editNextReportDateModal = useEditNextReportDateModal()

const tableRef = ref<HTMLDivElement | null>(null)
const pebbleTable = ref<PebbleTable | null>(null)

const tableRowData = computed(() => {
    if (!data.value?.latestConditionStatuses) {
        return []
    }

    return data.value.latestConditionStatuses
})

const showNextReportDateColumn = computed(() => {
    return componentType.value !== ComponentType.Generator
})

watch([tableRef, tableRowData, locale], ([newTableRef, newTableRowData]) => {
    if (!newTableRef) {
        return
    }

    const tabulatorOptions: Options = {
        height: props.isExportLayout ? tableHeights.exportLayout : tableHeights.normal,
        layout: props.isExportLayout ? 'fitColumns' : 'fitDataTable',
        pagination: false,
        reactiveData: true,
        data: newTableRowData,
        columnDefaults: {
            vertAlign: 'middle',
        },
        columns: [
            {
                title: t('transformers.conditionStatus.content.latestReports.table.date'),
                field: 'date',
                formatter: (cell: CellComponent) => {
                    return format(new Date(cell.getValue()), DATE_FORMAT)
                },
            },
            {
                title: t('transformers.conditionStatus.content.latestReports.table.reportType'),
                field: 'reportType',
                formatter: (cell: CellComponent) => {
                    const record = [...AnalysisTypes, ...MeasurementTypes].find((e) => e.value === cell.getValue())

                    return record ? t(record.labelKey) : '-'
                },
            },
            {
                title: t('transformers.conditionStatus.content.latestReports.table.reportId'),
                field: 'reportId',
            },
            {
                title: t('transformers.conditionStatus.content.latestReports.table.status'),
                field: 'status',
                formatter: (cell: CellComponent) => {
                    const record = StatusTypes.find((e) => e.value === cell.getValue())

                    if (!record) {
                        return '-'
                    }

                    cell.getElement().style.backgroundColor = record.color
                    cell.getElement().style.color = 'white'
                    cell.getElement().style.fontWeight = '500'

                    return t(record.labelKey)
                },
            },
            {
                visible: showNextReportDateColumn.value,
                title: t('transformers.conditionStatus.content.latestReports.table.nextReportDate'),
                field: 'nextReportDate',
                formatter: (cell: CellComponent) => {
                    const displayedText = format(new Date(cell.getValue()), DATE_FORMAT)

                    const cellDiv = document.createElement('div')
                    cellDiv.className = 'latest-condition-status-table-content-cell'

                    const textSpan = document.createElement('span')
                    textSpan.textContent = displayedText
                    cellDiv.appendChild(textSpan)

                    if (userPermissions.value.canEditTransformerRecommendations) {
                        const commentIcon = createElement(Pencil)
                        commentIcon.classList.add('latest-condition-status-table-icon')
                        cellDiv.appendChild(commentIcon)
                    }

                    return cellDiv
                },
                contextMenu: [
                    {
                        label: (): HTMLElement => {
                            const cellDiv = document.createElement('div')
                            cellDiv.classList.add('latest-condition-status-table-context-menu-item')

                            const iconDiv = createElement(Pencil)
                            cellDiv.appendChild(iconDiv)

                            const spanDiv = document.createElement('span')
                            spanDiv.textContent = t('transformers.conditionStatus.content.editNextReportDateModal.title')
                            cellDiv.appendChild(spanDiv)

                            return cellDiv
                        },
                        action: (e, cell) => {
                            const nextReportDate = cell.getValue()

                            const reportType = cell.getRow().getData().reportType
                            const reportId = cell.getRow().getData().reportId

                            editNextReportDateModal?.openModal({
                                reportType,
                                reportId,
                                nextReportDate,
                            })
                        },
                        disabled: () => !userPermissions.value.canEditTransformerRecommendations,
                    },
                ],
            },
        ],
        initialSort: [{ column: 'date', dir: 'desc' }],
    }

    pebbleTable.value?.destroy()
    pebbleTable.value = new PebbleTable(newTableRef, tabulatorOptions)
})
</script>

<style scoped lang="scss">
.latest-reports {
    margin: -($spacing-s);
}
</style>

<style lang="scss">
.latest-reports-link-cell {
    color: $pbl-primary;
    cursor: pointer;
}

.latest-condition-status-table-content-cell {
    display: flex;
    align-items: center;
    gap: 0.5em;
}

.latest-condition-status-table-icon {
    height: 1rem;
    width: 1rem;
}

.latest-condition-status-table-context-menu-item {
    display: flex;
    align-items: center;
    gap: $spacing-xs;

    padding: $spacing-3xs;

    span {
        font-weight: 500;
    }

    svg {
        height: 1rem;
        width: 1rem;
    }
}
</style>
